@if (userSessionService.activeOrganization(); as org) {

	<div class="row g-3 mb-3">
		<!-- Community Info -->
		<div [class]="(showMemberCard() ? 'col-12 col-xl-6' : 'col-12') + ' d-flex'">
			<a [routerLink]="['/organizations/profile', org.organizationId]" class="text-decoration-none w-100">
				<div class="card h-100 org-info-card">
					<div class="card-body d-flex align-items-center gap-3 p-3 p-md-4">
						<div class="rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 d-none d-sm-flex" style="width: 52px; height: 52px; min-width: 52px;">
							<fa-duotone-icon icon="buildings" class="text-primary" size="lg" secondaryOpacity="0.5" />
						</div>
						<div class="flex-grow-1">
							<h5 class="mb-0 fw-bold">{{ org.name }}</h5>
						</div>
						<fa-duotone-icon icon="arrow-right" class="org-info-chevron text-muted" secondaryOpacity="0.9" />
					</div>
				</div>
			</a>
		</div>

		<!-- Member Profile Card -->
		@if (showMemberCard()) {
			<div class="col-12 col-xl-6 d-flex">
				<ms-dashboard-member-profile-card
					[member]="member()"
					[profileLink]="memberProfileLink()"
					[isLoading]="isLoadingMember()" />
			</div>
		}
	</div>

	<!-- ===== ADMIN SECTIONS (permission-gated, prevents rendering + API calls) ===== -->

	<!-- Stat Cards -->
	@if (hasAdminPermissions()) {
		<div class="row g-3 mb-3">
			<div class="col-6" *ifHasPermission="{ requiredPermissions: [MemberPermission.Read] }">
				<ms-dashboard-stat-card
					title="Members"
					[value]="orgStats()?.memberCount"
					[icon]="DashboardConstants.ICON_MEMBERS"
					colorClass="text-success"
					routerLink="/members"
					[isLoading]="isLoadingStats()" />
			</div>
			<div class="col-6" *ifHasPermission="{ requiredPermissions: [PropertyPermission.Read] }">
				<ms-dashboard-stat-card
					title="Properties"
					[value]="orgStats()?.propertyCount"
					[icon]="DashboardConstants.ICON_PROPERTIES"
					colorClass="text-warning"
					routerLink="/properties"
					[isLoading]="isLoadingStats()" />
			</div>
		</div>

		<!-- Quick Actions -->
		<div class="row g-3 mb-3">
			<div class="col-12">
				<div class="card h-100">
					<div class="card-header bg-transparent border-0 pt-3 pt-md-4 px-3 px-md-4 pb-0">
						<h5 class="mb-0 fw-semibold">Quick Actions</h5>
					</div>
					<div class="card-body px-3 px-md-4 pt-2 pt-md-3 pb-3 pb-md-4">
						<ms-dashboard-quick-actions [actions]="quickActions" />
					</div>
				</div>
			</div>
		</div>
	}

	<!-- Charts -->
	@if (canReadBalances() || canReadMembers()) {
		<div class="row g-3 mb-3">
			<div class="col-12 col-xl-6" *ifHasPermission="{ requiredPermissions: [BalancesPermission.Read] }">
				@if (invoiceStatusData(); as cfg) {
					<ms-dashboard-chart
						title="Invoice Status"
						[config]="cfg"
						[isLoading]="isLoadingInvoiceStatus()" />
				} @else if (isLoadingInvoiceStatus()) {
					<ms-dashboard-chart
						title="Invoice Status"
						[isLoading]="true" />
				}
			</div>

			<div class="col-12 col-xl-6" *ifHasPermission="{ requiredPermissions: [MemberPermission.Read] }">
				@if (memberStatusData(); as cfg) {
					<ms-dashboard-chart
						title="Member Status"
						[config]="cfg"
						[isLoading]="isLoadingMemberStatus()" />
				} @else if (isLoadingMemberStatus()) {
					<ms-dashboard-chart
						title="Member Status"
						[isLoading]="true" />
				}
			</div>

		</div>
	}

	<!-- Admin List -->
	<div class="mb-3" *ifHasPermission="{ requiredPermissions: [RolePermission.OrgAdmin] }">
		<ms-dashboard-admin-list />
	</div>

	<!-- Recent Lists -->
	@if (canReadMembers() || canReadProperties()) {
		<div class="row g-3 mb-3 align-items-stretch">
			<div *ifHasPermission="{ requiredPermissions: [MemberPermission.Read] }"
				[class]="(canReadProperties() ? 'col-12 col-xl-6' : 'col-12') + ' d-flex'">
				<ms-dashboard-recent-list class="w-100"
					title="Recent Member Activity"
					[items]="recentMembers()"
					[isLoading]="isLoadingMembers()"
					emptyMessage="No members in this organization." />
			</div>
			<div *ifHasPermission="{ requiredPermissions: [PropertyPermission.Read] }"
				[class]="(canReadMembers() ? 'col-12 col-xl-6' : 'col-12') + ' d-flex'">
				<ms-dashboard-recent-list class="w-100"
					title="Recent Property Activity"
					[items]="recentProperties()"
					[isLoading]="isLoadingProperties()"
					emptyMessage="No properties in this organization." />
			</div>
		</div>
	}

	<!-- Divider between admin and personal sections -->
	@if (hasAdminPermissions()) {
		<hr class="my-4">
	}

	<!-- ===== PERSONAL SECTIONS (all org members) ===== -->

	<!-- Invoice Summary Cards -->
	<div class="row g-3 mb-3">
		<div class="col-4">
			<ms-dashboard-stat-card
				title="Open Invoices"
				[value]="invoiceSummary()?.openCount"
				icon="file-invoice"
				colorClass="text-info"
				[isLoading]="isLoadingInvoiceSummary()" />
		</div>
		<div class="col-4">
			<ms-dashboard-stat-card
				title="Overdue Invoices"
				[value]="invoiceSummary()?.lateCount"
				icon="triangle-exclamation"
				colorClass="text-warning"
				[isLoading]="isLoadingInvoiceSummary()" />
		</div>
		<div class="col-4">
			<ms-dashboard-stat-card
				title="Paid Invoices"
				[value]="invoiceSummary()?.paidCount"
				icon="circle-check"
				colorClass="text-success"
				[isLoading]="isLoadingInvoiceSummary()" />
		</div>
	</div>

	<!-- Outstanding Invoices -->
	<div class="card mb-3">
		<div class="card-body p-3 p-md-4">
			<div class="d-flex justify-content-between align-items-center mb-2">
				<h5 class="fw-semibold mb-0">Outstanding Invoices</h5>
				<span class="d-none d-sm-block"
					[msTooltip]="onlinePaymentsDisabledTooltip"
					[msTooltipDisabled]="isStripeOnboardingComplete()"
					[attr.tabindex]="isStripeOnboardingComplete() ? -1 : 0">
					<button class="btn btn-success btn-width-medium"
						[disabled]="selectedInvoiceAmount() === 0 || !isStripeOnboardingComplete()"
						(click)="payCharges()">
						Pay@if (selectedInvoiceAmount() > 0) { {{ ' ' + (selectedInvoiceAmount() | CurrencyDisplay: currencyType()!) }} }
					</button>
				</span>
			</div>
			<!-- Gated on memberId so the list's ngOnInit always has a member to filter by. Without the
			     gate, a member with no membership record for the active org would fall through the
			     component's `if (this.memberId())` check and fire an unfiltered org-wide charge query. -->
			@if (memberId()) {
				<ms-property-charge-list
					#chargeList
					[currentPropertyChargeListViewLocation]="PropertyChargeListViewLocations.MemberProfilePage"
					[memberId]="memberId()"
					(totalSelectedAmountChange)="selectedInvoiceAmount.set($event)"
					(totalCurrencyChange)="currencyType.set($event)"
					(payRequested)="payCharges()">
				</ms-property-charge-list>
			}
		</div>
	</div>

	<!-- My Properties -->
	<div class="mb-3">
		<ms-dashboard-recent-list
			title="My Properties"
			[items]="myProperties()"
			[isLoading]="isLoadingMyProperties()"
			emptyMessage="You don't have any properties assigned yet." />
	</div>

	<!-- Payment Modal -->
	<ms-payment-modal
		#paymentModal
		[userId]="userSessionService.activeUser()?.userId!"
		[organizationId]="org.organizationId"
		(paymentCompleted)="onPaymentCompleted()">
	</ms-payment-modal>

} @else if (userSessionService.hasInitializedOrganization()) {
	<ms-dashboard-select-org-prompt />
} @else {
	<div class="d-flex justify-content-center align-items-center py-5">
		<div class="spinner-border text-primary" role="status">
			<span class="visually-hidden">Loading organization...</span>
		</div>
	</div>
}
